// In-world markers: glowing pickup rings, the drop-off ring, the arrow above the player's cab, and the
// 2D minimap. Cheap meshes, one material each, updated every frame from match state.

import { Scene } from '@babylonjs/core/scene';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Fare } from '../sim/fares';
import type { Match, PlayerState } from '../sim/match';
import type { Ground } from '../sim/ground';
import type { WorldData } from './world';
import type { PeopleLibrary, PersonView } from './people';
import { PASSENGERS } from '../sim/fares';

export class Markers {
  private ringProto: Mesh;
  private rings = new Map<number, Mesh>();
  private dropRing: Mesh;
  private arrow: TransformNode;
  private arrowMesh: Mesh;
  private t = 0;
  private waiting = new Map<number, PersonView>();
  constructor(readonly scene: Scene, readonly ground: Ground, readonly people: PeopleLibrary | null = null) {
    const mk = (name: string, colour: Color3) => {
      const m = MeshBuilder.CreateTorus(name, { diameter: 8, thickness: 0.5, tessellation: 32 }, scene);
      const mat = new StandardMaterial(`${name}_mat`, scene);
      mat.emissiveColor = colour; mat.diffuseColor = colour; mat.disableLighting = true; mat.alpha = 0.9;
      m.material = mat; m.isPickable = false; return m;
    };
    this.ringProto = mk('pickupRing', new Color3(1, 0.78, 0.1)); this.ringProto.isVisible = false;
    this.dropRing = mk('dropRing', new Color3(1, 0.78, 0.1)); this.dropRing.setEnabled(false);
    this.arrow = new TransformNode('arrowRoot', scene);
    // Solid, single-headed arrow: separate face vertices preserve crisp box edges.
    const shape = [[0, 1.6], [-1, .35], [-.4, .35], [-.4, -1.2], [.4, -1.2], [.4, .35], [1, .35]];
    const triangles = [0, 1, 2, 0, 2, 5, 0, 5, 6, 2, 3, 4, 2, 4, 5];
    const positions: number[] = [], indices: number[] = [], colours: number[] = [];
    const face = (points: number[][], colour: number[]) => {
      const offset = positions.length / 3;
      for (const p of points) { positions.push(...p); colours.push(...colour, 1); }
      for (let i = 1; i < points.length - 1; i++) indices.push(offset, offset + i, offset + i + 1);
    };
    for (let i = 0; i < triangles.length; i += 3) {
      const t = triangles.slice(i, i + 3);
      face([...t].reverse().map(v => [shape[v][0], .3, shape[v][1]]), [1, .81, .04]);
      face(t.map(v => [shape[v][0], -.3, shape[v][1]]), [.68, .39, .015]);
    }
    for (let i = 0; i < shape.length; i++) {
      const a = shape[i], b = shape[(i + 1) % shape.length];
      face([[a[0], .3, a[1]], [b[0], .3, b[1]], [b[0], -.3, b[1]], [a[0], -.3, a[1]]], [.92, .60, .025]);
    }
    this.arrowMesh = new Mesh('arrow', scene);
    const data = new VertexData(); data.positions = positions; data.indices = indices; data.colors = colours;
    data.normals = []; VertexData.ComputeNormals(positions, indices, data.normals); data.applyToMesh(this.arrowMesh);
    const am = new StandardMaterial('arrow_mat', scene); am.diffuseColor = Color3.White();
    am.disableLighting = true; am.diffuseColor = Color3.Black(); am.emissiveColor = Color3.White(); am.specularColor = new Color3(.15, .15, .1);
    am.backFaceCulling = false; this.arrowMesh.material = am; this.arrowMesh.parent = this.arrow; this.arrowMesh.isPickable = false;
    this.arrow.setEnabled(false);
  }

  update(match: Match, me: PlayerState | null, dt: number): void {
    this.t += dt;
    const pulse = 1 + 0.08 * Math.sin(this.t * 4);
    const showPickups = !me?.riding.length;
    const seen = new Set<number>();
    for (const f of match.fares) {
      if (f.state !== 'waiting') continue;
      seen.add(f.id);
      let r = this.rings.get(f.id);
      if (!r) { r = this.ringProto.createInstance(`ring${f.id}`) as unknown as Mesh; r.position.set(f.pickup.x, this.ground.heightAt(f.pickup.x, f.pickup.z) + 0.25, f.pickup.z); this.rings.set(f.id, r); }
      r.position.set(f.pickup.x, this.ground.heightAt(f.pickup.x, f.pickup.z) + 0.35, f.pickup.z);
      r.isVisible = true; r.setEnabled(showPickups); r.scaling.setAll(pulse); r.rotation.y = this.t;
      if (this.people) {
        let w = this.waiting.get(f.id);
        if (!w) { const pi = PASSENGERS.findIndex((p) => p.id === f.passenger); w = this.people.spawn(pi * 5 + f.id, pi, undefined); w.place(f.pickup.x + 2.2, this.ground.heightAt(f.pickup.x + 2.2, f.pickup.z), f.pickup.z, 0); this.waiting.set(f.id, w); }
        // face the nearest cab and wave it down
        if (me) { w.root.rotation.y = Math.atan2(me.car.x - w.root.position.x, me.car.z - w.root.position.z); }
        if (!w.play('Wave', 1)) w.wave(this.t);
      }
    }
    for (const [id, r] of this.rings) if (!seen.has(id)) { r.dispose(); this.rings.delete(id); }
    for (const [id, w] of this.waiting) if (!seen.has(id)) { w.dispose(); this.waiting.delete(id); }

    const fare = me?.riding[0] ?? null;
    if (me && fare) {
      this.dropRing.setEnabled(true);
      this.dropRing.position.set(fare.dest.x, this.ground.heightAt(fare.dest.x, fare.dest.z) + 0.3, fare.dest.z);
      this.dropRing.scaling.setAll(1.3 * pulse);
      this.arrow.setEnabled(true);
      this.arrow.position.set(me.car.x + Math.sin(me.car.yaw) * 2, me.car.y + 3.4 + 0.1 * Math.sin(this.t * 3), me.car.z + Math.cos(me.car.yaw) * 2);
      const target = new Vector3(fare.dest.x, this.arrow.position.y, fare.dest.z);
      this.arrow.lookAt(target);
    } else if (me) {
      // point at the nearest waiting fare
      let best: Fare | null = null, bd = Infinity;
      for (const f of match.fares) if (f.state === 'waiting') { const d = Math.hypot(f.pickup.x - me.car.x, f.pickup.z - me.car.z); if (d < bd) { bd = d; best = f; } }
      this.dropRing.setEnabled(false);
      if (best && bd > 25) { this.arrow.setEnabled(true); this.arrow.position.set(me.car.x + Math.sin(me.car.yaw) * 2, me.car.y + 3.4 + 0.1 * Math.sin(this.t * 3), me.car.z + Math.cos(me.car.yaw) * 2); this.arrow.lookAt(new Vector3(best.pickup.x, this.arrow.position.y, best.pickup.z)); }
      else this.arrow.setEnabled(false);
    } else { this.dropRing.setEnabled(false); this.arrow.setEnabled(false); }
    if (me && this.arrow.isEnabled()) {
      // Tip the solid sign toward the chase camera so its head is readable even
      // when pointing straight ahead; yaw still tracks the world-space destination.
      const relative = me.car.yaw - this.arrow.rotation.y;
      this.arrowMesh.rotation.x = -Math.cos(relative) * .65;
      this.arrowMesh.rotation.z = Math.sin(relative) * .65;
    }

  }
}

/** Circular minimap drawn on a 2D canvas: roads from the ground mask, players, fares, boundary. */
export class Minimap {
  private ctx: CanvasRenderingContext2D;
  private roadLayer: HTMLCanvasElement;
  private scale = 2.6; // canvas px per metre at the base zoom (radius ~50 m)
  constructor(readonly canvas: HTMLCanvasElement, readonly ground: Ground, readonly world: WorldData) {
    this.ctx = canvas.getContext('2d')!;
    // pre-render the whole road mask once at 1 px per 2.5 m cell
    this.roadLayer = document.createElement('canvas');
    this.roadLayer.width = ground.cols; this.roadLayer.height = ground.rows;
    const c = this.roadLayer.getContext('2d')!; const img = c.createImageData(ground.cols, ground.rows);
    for (let i = 0; i < ground.types.length; i++) {
      const t = ground.types[i]; let r = 0, g = 0, b = 0, a = 0;
      if (t === 2) { r = 210; g = 210; b = 214; a = 255; } else if (t === 4 || t === 5) { r = 140; g = 140; b = 150; a = 255; } else if (t === 3) { r = 90; g = 96; b = 92; a = 255; } else if (t === 1) { r = 60; g = 96; b = 62; a = 255; } else if (t === 6) { r = 190; g = 172; b = 120; a = 255; } else { r = 40; g = 90; b = 140; a = 255; }
      img.data[i * 4] = r; img.data[i * 4 + 1] = g; img.data[i * 4 + 2] = b; img.data[i * 4 + 3] = a;
    }
    c.putImageData(img, 0, 0);
  }

  draw(match: Match, me: PlayerState | null, zoomOut = false): void {
    const ctx = this.ctx, W = this.canvas.width, H = this.canvas.height, cx = W / 2, cy = H / 2;
    const s = zoomOut ? this.scale * 0.45 : this.scale;
    const px = me?.car.x ?? 0, pz = me?.car.z ?? 0;
    ctx.clearRect(0, 0, W, H);
    ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, W / 2 - 3, 0, Math.PI * 2); ctx.clip();
    // world -> canvas: north (+z) up
    const toC = (x: number, z: number) => [cx + (x - px) * s, cy - (z - pz) * s] as const;
    // road layer: image pixel (c, r) covers x = origin+c*cell, z = origin+r*cell; flip z
    const cell = this.ground.cell; const [ox, oz] = toC(this.ground.origin[0], this.ground.origin[1]);
    ctx.imageSmoothingEnabled = true;
    ctx.save(); ctx.translate(ox, oz); ctx.scale(cell * s, -cell * s); ctx.drawImage(this.roadLayer, 0, 0); ctx.restore();
    // boundary
    const pa = this.world.play_area; ctx.strokeStyle = '#F2B90C'; ctx.lineWidth = 3; ctx.setLineDash([8, 6]);
    const [bx0, bz0] = toC(pa.x[0], pa.z[1]); const [bx1, bz1] = toC(pa.x[1], pa.z[0]); ctx.strokeRect(bx0, bz0, bx1 - bx0, bz1 - bz0); ctx.setLineDash([]);
    // fares
    for (const f of match.fares) {
      if (f.state === 'waiting') { const [x, y] = toC(f.pickup.x, f.pickup.z); dot(ctx, x, y, 5, '#fff', '#F2B90C'); }
    }
    const fare = me?.riding[0];
    if (fare) { const [x, y] = toC(fare.dest.x, fare.dest.z); dot(ctx, x, y, 7, '#4CC26B', '#fff'); }
    // players
    for (const p of match.players.values()) {
      if (p === me) continue;
      const [x, y] = toC(p.car.x, p.car.z); dot(ctx, x, y, 6, `#${p.colour}`, '#fff');
    }
    if (me) { ctx.save(); ctx.translate(cx, cy); ctx.rotate(me.car.yaw); ctx.fillStyle = '#F2B90C'; ctx.strokeStyle = '#17181B'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(0, -11); ctx.lineTo(8, 8); ctx.lineTo(0, 4); ctx.lineTo(-8, 8); ctx.closePath(); ctx.fill(); ctx.stroke(); ctx.restore(); }
    ctx.restore();
    ctx.strokeStyle = 'rgba(255,255,255,.8)'; ctx.lineWidth = 4; ctx.beginPath(); ctx.arc(cx, cy, W / 2 - 3, 0, Math.PI * 2); ctx.stroke();
  }
}

function dot(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, fill: string, stroke: string): void {
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fillStyle = fill; ctx.fill(); ctx.strokeStyle = stroke; ctx.lineWidth = 2; ctx.stroke();
}
